import { useState } from 'react';
import { Box } from '@mui/material';
import { format, getYear } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { SttButtonNew, SttTable, SttTopControls } from '../../../sporttia/all';
import { useTuples, useCrud } from '../../../lib/hooks';
import RestrictionUserDialog from './RestrictionUserDialog';
import { fullName } from '../../../lib/utils';
import DateBuilder from '../../../lib/DateBuilder';
import { PERMISSIONS } from '../../../lib/permissions';

type User = {
    id?: string;
    name?: string;
    surname?: string;
};

type UserRestriction = {
    id: string;
    user?: User;
    ini?: string;
    end?: string;
};

type Props = {
    id?: string;
    trash?: unknown;
};

export default function RestrictionUsers({ id }: Props): React.ReactElement {
    const { t } = useTranslation();

    const [userRestrictions, setUserRestrictions, , Get, Post, Put, Delete] =
        useCrud<any>();
    const [filters, setFilter] = useTuples({
        name: '',
    });

    const [viewRestriction, setViewRestriction] = useState<
        UserRestriction | undefined
    >(undefined);

    function loadUsers(params = { rows: 20 }) {
        Get(`/bookings/restrictions/${id}/users`, {
            ...params,
            ...filters,
        }).then(setUserRestrictions);
    }

    function saveRestriction(restriction: UserRestriction) {
        if (restriction.id) {
            Put(`/bookings/restrictions/users/${restriction.id}`, {
                ini: new DateBuilder(restriction.ini as any).ymd(),
                end: new DateBuilder(restriction.end as any).ymd(),
            }).then((_result: unknown) => {
                const data = userRestrictions as { rows: UserRestriction[] };
                setUserRestrictions({
                    ...userRestrictions,
                    rows: data.rows.map((item: any) =>
                        item.id !== restriction.id ? item : restriction,
                    ),
                });
                setViewRestriction(undefined);
            });
        } else {
            Post(`/bookings/restrictions/${id}/users/${restriction.user?.id}`, {
                ini: restriction.ini,
                end: restriction.end,
            }).then((result: any) => {
                if (result.bookingRestrictionUser) {
                    const data = userRestrictions as {
                        rows: UserRestriction[];
                        count: number;
                    };
                    setUserRestrictions({
                        count: data.count + 1,
                        rows: data.rows.concat([result.bookingRestrictionUser]),
                    });
                    setViewRestriction(undefined);
                }
            });
        }
    }

    function deleteRestriction(restriction: UserRestriction) {
        Delete(`/bookings/restrictions/users/${restriction.id}`).then(
            (_result: unknown) => {
                const data = userRestrictions as {
                    rows: UserRestriction[];
                    count: number;
                };
                setUserRestrictions({
                    count: data.count - 1,
                    rows: data.rows.filter(
                        (item: any) => item.id !== restriction.id,
                    ),
                });
                setViewRestriction(undefined);
            },
        );
    }

    return (
        <>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                p={2}
            >
                <SttTopControls
                    fields={
                        [
                            {
                                caption: t('Name'),
                                type: 'text',
                                name: 'name',
                                value: filters.name,
                                onEnterKey: loadUsers,
                            },
                        ] as any
                    }
                    onChange={setFilter}
                    onFilter={loadUsers}
                />
                <SttButtonNew
                    permission={PERMISSIONS.RESTRICTION.MODIFY}
                    onClick={() =>
                        setViewRestriction({
                            id: '',
                            ini: format(new Date(), 'yyyy-MM-dd'),
                            end: `${getYear(new Date())}-12-31`,
                            user: {
                                name: '',
                            },
                        })
                    }
                />
            </Box>
            <SttTable
                onFetch={loadUsers}
                data={userRestrictions}
                onClickRow={(row: UserRestriction) => setViewRestriction(row)}
                columns={[
                    {
                        title: t('Name'),
                        align: 'left',
                        type: 'text',
                        value: (row: UserRestriction) =>
                            row.user && fullName(row.user),
                    },
                    {
                        title: t('Begin'),
                        type: 'dmy',
                        field: 'ini',
                    },
                    {
                        title: t('End'),
                        type: 'dmy',
                        field: 'end',
                    },
                ]}
            />
            {viewRestriction && (
                <RestrictionUserDialog
                    {...({
                        item: viewRestriction,
                        onSave: saveRestriction,
                        onClose: () => setViewRestriction(undefined),
                        onDelete: deleteRestriction,
                    } as any)}
                />
            )}
        </>
    );
}
